// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import type { StrongOmit } from './_helpers/helper-types';
import { ignoreCanceledApiRequest } from './_helpers/isRequestError';
import {
  useWrappedApiCallNoBody,
  useWrappedApiCallWithBody,
  type Opts,
  type ApiCallReturn,
  type NoBodyApiCallArgs,
  type WithBodyApiCallArgs,
} from './_helpers/wrapped_api_call/useWrappedApiCall';

import type { EvolveApiRoute, RouteWithPathParam } from './ENDPOINTS';

export type GetOpts<Route extends EvolveApiRoute<'GET'>> = Opts<'GET', Route> &
  (Route extends RouteWithPathParam
    ? {
        /** Cannot set lazy when URL has missing route parameters. */
        lazy?: boolean;
      }
    : {
        /**
         * If false, will automatically make the API call
         * on load and on every config change.
         *
         * @defaultValue true
         * */
        lazy?: boolean;
      });

/** Used to make a GET request to an EVOLVE API. */
export const useEvolveGet = <Route extends EvolveApiRoute<'GET'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: GetOpts<Route>,
): StrongOmit<ApiCallReturn<'GET', Route, NoBodyApiCallArgs<'GET', Route>>, 'setDefaultOpts'> & {
  setDefaultOpts: (newOpts: GetOpts<Route>) => void;
} => {
  const {
    apiCall,
    setDefaultOpts: setDefaultOptsInner,
    loading,
    ...rest
  } = useWrappedApiCallNoBody<'GET', Route>('get', url, opts);

  const [lazy, setLazy] = useState(opts?.lazy ?? true);
  // Used to track if the original non-lazy call has been made
  const [initialCallMade, setInitialCallMade] = useState(lazy);
  useEffect(() => {
    if (!lazy) {
      // @ts-expect-error Because `url` MUST be the parameter-required version,
      // no params are required for `apiCall`; but TS can't seem to figure that out.
      const params = [] as NoBodyApiCallArgs<'GET', Route>;
      // Marked as made even when cancelled: a request that replaced this one keeps `loading` true on its own,
      // and with none in flight there is nothing left to wait for.
      void apiCall(...params)
        .catch(ignoreCanceledApiRequest)
        .finally(() => setInitialCallMade(true));
    }
  }, [apiCall, lazy]);

  const setDefaultOpts = useCallback(
    (newOpts: GetOpts<Route>) => {
      setDefaultOptsInner(newOpts);
      setLazy((l) => newOpts.lazy ?? l);
    },
    [setDefaultOptsInner],
  );

  return {
    apiCall,
    setDefaultOpts,
    loading: loading || !initialCallMade,
    ...rest,
  };
};

/** Used to make a POST request to an EVOLVE API. */
export const useEvolvePost = <Route extends EvolveApiRoute<'POST'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'POST', Route>,
): ApiCallReturn<'POST', Route, WithBodyApiCallArgs<'POST', Route>> =>
  useWrappedApiCallWithBody<'POST', Route>('post', url, opts);

/** Used to make a PATCH request to an EVOLVE API. */
export const useEvolvePatch = <Route extends EvolveApiRoute<'PATCH'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'PATCH', Route>,
): ApiCallReturn<'PATCH', Route, WithBodyApiCallArgs<'PATCH', Route>> =>
  useWrappedApiCallWithBody<'PATCH', Route>('patch', url, opts);

/** Used to make a PUT request to an EVOLVE API. */
export const useEvolvePut = <Route extends EvolveApiRoute<'PUT'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'PUT', Route>,
): ApiCallReturn<'PUT', Route, WithBodyApiCallArgs<'PUT', Route>> =>
  useWrappedApiCallWithBody<'PUT', Route>('put', url, opts);

/** Used to make a DELETE request to an EVOLVE API. */
export const useEvolveDelete = <Route extends EvolveApiRoute<'DELETE'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'DELETE', Route>,
): ApiCallReturn<'DELETE', Route, NoBodyApiCallArgs<'DELETE', Route>> =>
  useWrappedApiCallNoBody<'DELETE', Route>('apiDelete', url, opts);
